// @flow
import { call, put, takeLatest, select, take } from 'redux-saga/effects';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import {
  FETCH_MULE_RED_DOT_STATE_REQUEST,
  UPDATE_MULE_CUST_STATE_REQUEST,
  RESET_MULE_CUST_STATE_REQUEST,
  REFRESH_MULE_RED_DOT_STATE_REQUEST,
  FETCH_MULE_RED_DOT_STATE_SUCCESS,
  UPDATE_MULE_RED_DOT_REQUEST,
  FETCH_MULE_CUST_STATE_REQUEST,
  REFRESH_MULE_CUST_STATE_REQUEST,
  updateMuleCustState,
  fetchMuleCustStateRequest,
  FETCH_MULE_RED_DOT_STATE_FAILURE,
} from '../actions/muleCustStateActions';
import {
  getMuleRedDotState,
  getMuleRedDotError,
  getMuleCustStateChannel,
  getMuleCustStateGetUrl,
  getMuleCustStateSetUrl,
  getMuleRedDotCustKey,
} from '../selectors/muleCustStateSelectors';
import { myAxaFetchWithToken } from '../../../utils/myAxaFetchWithToken';
import {
  MuleRedDotFeatureItem,
  MuleRedDotFeatureList,
} from '../types/muleCustStateType';
import { FETCH_CUSTOMER_SUCCESS } from '../../../actions/userActions';

// -- RED DOT --
function* fetchMuleRedDotState(action: any): any {
  console.log('fetchRedDotState');
  const custKey = yield select(getMuleRedDotCustKey);
  yield put(
    fetchMuleCustStateRequest(
      custKey,
      FETCH_MULE_RED_DOT_STATE_SUCCESS,
      FETCH_MULE_RED_DOT_STATE_FAILURE,
    ),
  );
}

function* putMuleRedDotState(action: any): any {
  console.log('putRedDotState');
  const custKey = yield select(getMuleRedDotCustKey);
  const originalValue = yield select(getMuleRedDotState);
  const redDotError = yield select(getMuleRedDotError);
  const clickedFeatureIds: string[] = action.payload.clickedWebinarEventIds;
  // Don't do anything if original value is not retrieved or error occured. Just avoid to override the original value.
  if (
    originalValue == null ||
    redDotError ||
    !clickedFeatureIds ||
    clickedFeatureIds.length === 0
  ) {
    return;
  }

  let redDotFeatureList: MuleRedDotFeatureList;
  if (originalValue && !originalValue.features) {
    // Never created
    redDotFeatureList = {
      features: [],
    };
  } else {
    redDotFeatureList = { ...originalValue };
  }

  // Build red dot state value
  // This is to add click count to the original value
  const newClickedFeatures = [] as any[];
  clickedFeatureIds.forEach((id: string) => {
    const feature = redDotFeatureList.features.find((f) => f.featureId === id);

    if (feature) {
      feature.clickCount++;
    } else {
      const newClickedFeature: MuleRedDotFeatureItem = {
        featureId: id,
        clickCount: 1,
      };
      newClickedFeatures.push(newClickedFeature);
    }
  });
  redDotFeatureList.features =
    redDotFeatureList.features.concat(newClickedFeatures);

  const newValue = JSON.stringify(redDotFeatureList);
  const originalValueStr = JSON.stringify(originalValue);
  yield put(
    updateMuleCustState(
      custKey,
      originalValueStr,
      newValue,
      REFRESH_MULE_RED_DOT_STATE_REQUEST,
    ),
  );
}

/**
 * This saga function designed for general purpose. You can reuse it for your cust key.
 * @param {*} action
 */
function* fetchMuleCustState(action: any): any {
  console.log(`fetchMuleCustState ${action.type}:`);
  try {
    const getCustomerStateUrl = yield select(getMuleCustStateGetUrl);
    const channel = yield select(getMuleCustStateChannel);
    const custKey = action.custKey;
    const url = `${getCustomerStateUrl}`
      .replace(`\${custKey}`, custKey)
      .replace(`\${channel}`, channel);

    const res = yield call(myAxaFetchWithToken, url, {
      method: 'GET',
      headers: { 'x-axahk-msgid': uuidv4() },
    });
    if (res && res.status === 200) {
      const data = yield res.json();
      const custValue = data.length > 0 ? data[0].custValue : undefined;
      const readState = custValue ? JSON.parse(decodeURI(custValue)) : {};
      yield put({
        type: action.succcessAction,
        payload: readState,
      });
    } else {
      throw res;
    }
  } catch (e) {
    console.log(e);
    yield put({ type: action.failureAction, payload: e });
  }
}

/**
 * This saga function designed for general purpose. You can reuse it for your cust key.
 * original value must be passed to focus you follow the same logic checking
 * @param {*} action
 */
function* putMuleCustState(action: any): any {
  console.log('putMuleCustState');

  try {
    const newValue: string = action.payload.newValue;
    const originalValue: string = action.payload.originalValue;

    if (newValue) {
      const isReadStatusErrorCase = originalValue == null;
      if (!isReadStatusErrorCase) {
        const channel = yield select(getMuleCustStateChannel);
        const custKey = action.payload.custKey;
        const setUrl = yield select(getMuleCustStateSetUrl);
        const url = setUrl;

        if (!_.isEqual(originalValue, newValue)) {
          const res = yield call(myAxaFetchWithToken, url, {
            method: 'POST',
            headers: {
              'x-axahk-msgid': uuidv4(),
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              insertCustomerStateRequest: {
                channelCode: channel,
                custKey: custKey,
                custValue: encodeURI(newValue),
              },
            }),
          });

          if (res && res.status === 200) {
            yield put({
              type: action.refreshAction,
            });
          } else {
            console.log(res);
            throw res;
          }
        }
      }
    }
  } catch (e) {
    console.log(e);
  }
}

/**
 * This saga function designed for general purpose. You can reuse it for your cust key.
 * @param {*} action
 */
function* resetMuleCustState(action: any): any {
  console.log('resetMuleCustState');

  try {
    const setCustomerStateUrl = yield select(getMuleCustStateSetUrl);
    const channel = yield select(getMuleCustStateChannel);
    const custKey = action.custKey;
    const custValue = '';

    const setUrl = `${setCustomerStateUrl}`;
    const url = setUrl;
    const res = yield call(myAxaFetchWithToken, url, {
      method: 'POST',
      headers: { 'x-axahk-msgid': uuidv4(), 'Content-Type': '' },
      body: JSON.stringify({
        insertCustomerStateRequest: {
          channelCode: channel,
          custKey: custKey,
          custValue: custValue,
        },
      }),
    });

    if (res && res.status === 200) {
      yield put({
        type: action.refreshAction,
      });
    } else {
      console.log(res);
      throw res;
    }
  } catch (e) {
    console.log(e);
  }
}

function* muleCustStateSaga(): any {
  yield takeLatest(FETCH_CUSTOMER_SUCCESS, fetchMuleRedDotState);
  yield takeLatest(FETCH_MULE_RED_DOT_STATE_REQUEST, fetchMuleRedDotState);
  yield takeLatest(REFRESH_MULE_RED_DOT_STATE_REQUEST, fetchMuleRedDotState);
  yield takeLatest(UPDATE_MULE_RED_DOT_REQUEST, putMuleRedDotState);

  yield takeLatest(FETCH_MULE_CUST_STATE_REQUEST, fetchMuleCustState);
  yield takeLatest(REFRESH_MULE_CUST_STATE_REQUEST, fetchMuleCustState);
  yield takeLatest(UPDATE_MULE_CUST_STATE_REQUEST, putMuleCustState);
  yield takeLatest(RESET_MULE_CUST_STATE_REQUEST, resetMuleCustState);
}

export { muleCustStateSaga };
